import type { DrillSide, ProjectBasicInfo, ProjectDetail, SimulationState } from '@/types/api';
import { apiClient } from './client';

export interface ListProjectsParams {
  page?: number;
  size?: number;
  tag?: string[];
  simulationState?: SimulationState[];
  hasCollisions?: boolean | null;
  sortBy?: 'created_at' | 'name';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Fetches a paginated list of projects.
 * Returns items and total count from X-Total-Count header.
 */
export const listProjects = async (
  params: ListProjectsParams = {}
): Promise<{ items: ProjectBasicInfo[]; total: number }> => {
  const searchParams: Record<string, string | string[]> = {};

  if (params.page !== undefined) searchParams.page = String(params.page);
  if (params.size !== undefined) searchParams.size = String(params.size);
  if (params.tag?.length) searchParams.tag = params.tag;
  if (params.simulationState?.length) searchParams.simulation_state = params.simulationState;
  if (params.hasCollisions !== undefined && params.hasCollisions !== null) {
    searchParams.has_collisions = String(params.hasCollisions);
  }
  if (params.sortBy) searchParams.sort_by = params.sortBy;
  if (params.sortOrder) searchParams.sort_order = params.sortOrder;

  const response = await apiClient.get<ProjectBasicInfo[]>('/projects', { params: searchParams });
  const total = parseInt(response.headers['x-total-count'] ?? '0', 10);

  return { items: response.data, total };
};

/**
 * Fetches full project details including files and simulation results.
 */
export const getProject = async (projectId: string): Promise<ProjectDetail> => {
  const response = await apiClient.get<ProjectDetail>(`/projects/${projectId}`);
  return response.data;
};

/**
 * Deletes a project and all its associated data.
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  await apiClient.delete(`/projects/${projectId}`);
};

export interface UpdateProjectParams {
  name?: string | null;
  tags?: string[] | null;
}

export interface CreateProjectParams {
  targetModel: File;
  blankModel?: File;
  programFiles?: { file: File; drillSide: DrillSide }[];
}

export interface UploadModelFileParams {
  fileType: 'target' | 'blank';
  file: File;
}

/**
 * Updates project metadata (name, tags).
 */
export const updateProject = async (projectId: string, params: UpdateProjectParams): Promise<ProjectDetail> => {
  const response = await apiClient.patch<ProjectDetail>(`/projects/${projectId}`, params);
  return response.data;
};

/**
 * Uploads or replaces a model file (target or blank) for an existing project.
 */
export const uploadModelFile = async (projectId: string, params: UploadModelFileParams): Promise<ProjectDetail> => {
  const formData = new FormData();
  formData.append('file_type', params.fileType);
  formData.append('model_file', params.file);

  const response = await apiClient.post<ProjectDetail>(`/projects/${projectId}/model-files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Creates a new project by uploading target model, blank model and optional program files.
 */
export const createProject = async (params: CreateProjectParams): Promise<ProjectBasicInfo> => {
  const formData = new FormData();
  formData.append('target_model', params.targetModel);
  if (params.blankModel) {
    formData.append('blank_model', params.blankModel);
  }

  if (params.programFiles) {
    for (const { file, drillSide } of params.programFiles) {
      formData.append('program_file', file);
      formData.append('program_file_drill_side', drillSide);
    }
  }

  const response = await apiClient.post<ProjectBasicInfo>('/projects', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export interface StartCollisionDetectionResult {
  project: ProjectDetail;
  taskId: string;
}

/**
 * Starts collision detection for a project.
 * Returns the updated project and the background task ID for SSE progress tracking.
 */
export const startCollisionDetection = async (projectId: string): Promise<StartCollisionDetectionResult> => {
  const response = await apiClient.post<ProjectDetail>(`/projects/${projectId}/start-collision-detection`);
  const taskId = response.headers['x-background-task-id'];
  if (!taskId) throw new Error('Missing X-Background-Task-Id header');
  return { project: response.data, taskId };
};

/**
 * Downloads the simulation video for a project.
 * Returns a Blob that can be used as a video source.
 */
export const getSimulationVideo = async (projectId: string): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`/projects/${projectId}/simulation-video`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Downloads the simulation mesh as a GLB file.
 * Returns a Blob that can be used in a 3D viewer.
 */
export const getSimulationMesh = async (projectId: string): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`/projects/${projectId}/simulation-mesh`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Uploads program files to an existing project for a specific drill side.
 */
export const uploadProgramFiles = async (
  projectId: string,
  files: File[],
  drillSide: DrillSide
): Promise<ProjectDetail> => {
  const formData = new FormData();
  for (const file of files) {
    formData.append('program_file', file);
    formData.append('program_file_drill_side', drillSide);
  }

  const response = await apiClient.post<ProjectDetail>(`/projects/${projectId}/program-files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Moves a program file to a new position within the same drill side group.
 * @param afterId - The ID of the file to place this file after. Null means move to first position.
 */
export const moveProgramFile = async (
  projectId: string,
  programFileId: string,
  afterId: string | null
): Promise<ProjectDetail> => {
  const response = await apiClient.post<ProjectDetail>(`/projects/${projectId}/program-files/${programFileId}/move`, {
    after: afterId,
  });
  return response.data;
};

/**
 * Cancels the running collision detection task for a project.
 */
export const cancelCollisionDetection = async (projectId: string): Promise<ProjectDetail> => {
  const response = await apiClient.post<ProjectDetail>(`/projects/${projectId}/cancel-collision-detection`);
  return response.data;
};
